'use client';

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, X, ClipboardList } from "lucide-react";
import specsdictionary from "../../specsdictionary.json";

export default function VariantDetails({ car, isLinkStyle, triggerText, buttonClassName }) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!car) return null;

  // Categories we want to extract from the car object
  const categories = [
    { key: "technical_info", label: "مشخصات فنی" },
    { key: "exterior", label: "مشخصات ظاهری" },
    { key: "interior", label: "امکانات رفاهی و داخلی" },
    { key: "safety", label: "امکانات ایمنی" },
    { key: "adas", label: "سیستم‌های همیار راننده (ADAS)" },
  ];

  // Render a single accordion category
  const renderCategory = (category) => {
    const dataObj = car[category.key];
    if (!dataObj || typeof dataObj !== 'object') return null;

    // Filter valid fields
    const validFields = Object.keys(dataObj)
      .map((k) => {
        const val = dataObj[k];
        if (val !== null && val !== undefined && val !== "" && specsdictionary[k]) {
          return { key: k, label: specsdictionary[k], value: val };
        }
        return null;
      })
      .filter(Boolean);

    if (validFields.length === 0) return null;

    const isExpanded = expandedCategory === category.key;

    return (
      <div key={category.key} className="border-b border-gray-200">
        <button
          onClick={() => setExpandedCategory(isExpanded ? null : category.key)}
          className="w-full flex items-center justify-between py-5 bg-transparent hover:text-gray-600 transition-colors cursor-pointer"
        >
          <span className="font-semibold text-gray-900 text-lg">{category.label}</span>
          <span className="text-gray-400">
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </span>
        </button>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pb-6 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5">
                {validFields.map((field) => (
                  <div key={field.key} className="flex flex-col border-b border-gray-100 pb-2">
                    <span className="text-gray-500 text-sm mb-1">{field.label}</span>
                    <span className="font-semibold text-gray-900 text-base">
                      {field.value === true ? 'دارد' : field.value === false ? 'ندارد' : field.value}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Extract values for the gray box (checking both exterior and technical_info just in case)
  const getVal = (key) => car.exterior?.[key] || car.technical_info?.[key];

  const length = getVal('length_mm') ? `${getVal('length_mm')} mm` : "-";
  const width = getVal('width_mm') ? `${getVal('width_mm')} mm` : "-";
  const wheelbase = getVal('wheelbase_mm') ? `${getVal('wheelbase_mm')} mm` : "-";
  const carImage = car.detail_image_5 || car.main_image || car.slider_image || "";

  const triggerButton = isLinkStyle ? (
    <button
      onClick={() => setIsOpen(true)}
      className="text-sm font-semibold underline text-gray-900 hover:text-blue-600 transition-colors text-right cursor-pointer w-full block"
    >
      {triggerText || "مشخصات فنی"}
    </button>
  ) : (
    <div className="flex justify-start w-full">
      <button
        onClick={() => setIsOpen(true)}
        className={buttonClassName || "w-full justify-center px-8 py-3 ml-7 mt-5 bg-gray-300 hover:bg-gray-100 text-[#2b2b2b] font-bold rounded-button-mob md:rounded-button-desk shadow-lg transition-transform transform hover:scale-105 flex items-center gap-2 cursor-pointer"}
      >
        <span>{triggerText || "مشخصات فنی"}</span>
        {!buttonClassName && <ClipboardList className="w-5 h-5" />}
      </button>
    </div>
  );

  return (
    <>
      {triggerButton}

      {/* Sidebar Overlay and Drawer */}
      {mounted && createPortal(
        <AnimatePresence>
          {isOpen && (
            <div className="fixed inset-0 z-[999999] pointer-events-none" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%' }}>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
              />

              {/* Sidebar / Modal */}
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="absolute top-0 right-0 h-full w-[100%] md:w-[85%] lg:w-[75%] bg-white/80 backdrop-blur-2xl shadow-2xl flex flex-col pointer-events-auto"
              >
                {/* Sidebar Header (Fixed at top of flex column) */}
                <div className="flex-shrink-0 px-8 py-6 flex items-center justify-between z-10 border-b border-gray-200/30">
                  <div className="text-gray-800 text-lg md:text-xl font-medium latinNumbers">
                    {car.english_name}
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 text-gray-500 hover:text-gray-900 transition cursor-pointer"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Sidebar Content (Scrolling body) */}
                <div className="flex-1 overflow-y-auto px-8 md:px-16 pt-8 pb-16 w-full">
                  <div className="max-w-6xl mx-auto w-full">

                    {/* Tabs (Visual to match sample) */}
                    <div className="flex gap-2 mb-8">
                      <div className="bg-[#E4E4E6] text-gray-900 px-6 py-2.5 rounded-small-button-mob md:rounded-small-button-desk text-sm font-semibold">
                        مشخصات فنی
                      </div>
                      <div className="bg-transparent text-gray-600 px-6 py-2.5 rounded-small-button-mob md:rounded-small-button-desk text-sm font-semibold hover:bg-gray-100 transition-colors cursor-pointer">
                        امکانات استاندارد
                      </div>
                    </div>

                    {/* Gray Box with Image and Specs */}
                    <div className="bg-white rounded-card-mob md:rounded-card-desk p-10 mb-12 flex flex-col md:flex-row items-center gap-10">
                      <div className="w-full md:w-1/2 flex justify-center min-h-[150px]">
                        {carImage ? (
                          <img src={carImage} alt={car.english_name} className="w-full h-auto object-contain max-h-[300px]" />
                        ) : (
                          <div className="w-full h-40 flex items-center justify-center text-gray-400">
                            تصویری موجود نیست
                          </div>
                        )}
                      </div>
                      <div className="w-full md:w-1/2 grid grid-cols-2 gap-y-10 gap-x-8 text-right">
                        <div>
                          <div className="text-gray-500 text-sm mb-1 font-medium">طول</div>
                          <div className="text-2xl font-semibold text-gray-900 latinNumbers">{length}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-sm mb-1 font-medium">فاصله بین دو محور</div>
                          <div className="text-2xl font-semibold text-gray-900 latinNumbers">{wheelbase}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 text-sm mb-1 font-medium">عرض</div>
                          <div className="text-2xl font-semibold text-gray-900 latinNumbers">{width}</div>
                        </div>
                      </div>
                    </div>

                    {/* Accordions */}
                    <div className="flex flex-col gap-0">
                      {categories.map(renderCategory)}

                      {(!car.technical_info && !car.exterior && !car.interior && !car.safety) && (
                        <div className="text-center text-gray-500 py-20">
                          اطلاعات دقیقی برای این مدل ثبت نشده است.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
